import { PostHogProvider } from "posthog-js/react";
import type { ReactNode } from "react";

import rootPackage from "../../../package.json";

/**
 * localStorage key used to opt-in to PostHog analytics during local development.
 */
export const ANALYTICS_ENABLED_KEY = "bd-analytics-enabled";

interface AnalyticsProviderProps {
  children: ReactNode;
  /** Defaults to monorepo root version when omitted. */
  widgetVersion?: string;
}

export function AnalyticsProvider({ children, widgetVersion }: AnalyticsProviderProps) {
  const version = widgetVersion ?? (rootPackage as { version: string }).version;
  const apiKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;

  if (import.meta.env.VITE_PLAYWRIGHT_TEST === "true") {
    return <>{children}</>;
  }

  if (import.meta.env.DEV) {
    const isOptedIn = localStorage.getItem(ANALYTICS_ENABLED_KEY) === "true";
    if (!isOptedIn) {
      return <>{children}</>;
    }
  }

  if (!apiKey) {
    throw new Error("VITE_PUBLIC_POSTHOG_KEY environment variable is required for analytics");
  }

  return (
    <PostHogProvider
      apiKey={apiKey}
      options={{
        api_host: "https://us.i.posthog.com",
        capture_exceptions: false,
        debug: import.meta.env.VITE_DEBUG === "true",
        capture_pageview: true,
        capture_pageleave: true,
        loaded: (posthog) => {
          posthog.register({
            $host: window.location.host,
            $origin: window.location.origin,
            $protocol: window.location.protocol,
            widget_version: version,
          });
        },
      }}
    >
      {children}
    </PostHogProvider>
  );
}
